import { useTranslation } from "react-i18next";
import type { OntologyModule } from "../../../api/content/ontologyApi";
import { classNames } from "../../../ui/styles/classNames";
import { chipClassName } from "../../../ui/styles/chipStyles";
import { moduleGroups } from "./moduleGroups";

interface ModuleListProps {
  modules: readonly OntologyModule[];
  chosenModuleId: string | null;
  onChoose: (moduleId: string | null) => void;
}

/** The ontology modules the version sees: the project's own first, where "All" shows their
 * terms, then each library it inherits, such as the DMW basis (D-08, H-09), read-only. Choosing
 * one module shows only its terms. */
export default function ModuleList({ modules, chosenModuleId, onChoose }: ModuleListProps) {
  const { t } = useTranslation();
  const { own, inherited } = moduleGroups(modules);
  const choice = (moduleId: string | null, label: string, title: string) => (
    <li key={moduleId ?? "all"}>
      <button
        type="button"
        aria-pressed={chosenModuleId === moduleId}
        title={title}
        className={classNames(
          chipClassName(),
          chosenModuleId === moduleId ? "app-text-accent font-semibold" : "app-action-link",
        )}
        onClick={() => {
          onChoose(moduleId);
        }}
      >
        {label}
      </button>
    </li>
  );
  return (
    <nav aria-label={t("ontologyViewer.modules.title")} className="flex flex-wrap items-end gap-x-6 gap-y-2">
      {own.length > 0 ? (
        <div>
          <p className="app-label mb-1 text-xs">{t("ontologyViewer.modules.title")}</p>
          <ul className="flex flex-wrap gap-1.5">
            {choice(null, t("ontologyViewer.modules.all", { count: own.length }), t("ontologyViewer.modules.allHint"))}
            {own.map((module) => choice(module.itemId, module.name, module.reference))}
          </ul>
        </div>
      ) : null}
      {inherited.map((group) => (
        <div key={group.libraryProjectId}>
          <p className="app-label mb-1 text-xs" title={group.libraryVersion}>
            {t(group.isDmwBasis ? "ontologyViewer.modules.dmwBasis" : "ontologyViewer.modules.library")}
          </p>
          <ul className="flex flex-wrap gap-1.5">{group.modules.map((module) => choice(module.itemId, module.name, module.reference))}</ul>
        </div>
      ))}
    </nav>
  );
}
